import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { useLenders, useBlockLender, useUnblockLender } from '../../hooks/useLenders';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDateShort } from '../../utils/date';
import type { Lender } from '../../api/lenders';

const columnHelper = createColumnHelper<Lender>();

export default function LendersListPage() {
  const navigate = useNavigate();
  const { data: lenders, isLoading } = useLenders();
  const blockLender = useBlockLender();
  const unblockLender = useUnblockLender();

  const handleToggleStatus = async (e: React.MouseEvent, lender: Lender) => {
    e.stopPropagation();
    const isActive = (lender.status || 'active') === 'active';
    if (isActive) {
      await blockLender.mutateAsync(lender.urn);
    } else {
      await unblockLender.mutateAsync(lender.urn);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{info.getValue()}</span>,
      }),
      columnHelper.accessor('displayName', {
        header: 'Display Name',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() || 'active';
          return <StatusBadge status={status} />;
        },
      }),
      columnHelper.accessor('urn', {
        header: 'URN',
        cell: (info) => <span className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate max-w-[250px] block">{info.getValue()}</span>,
      }),
      columnHelper.accessor('products', {
        header: 'Products',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{info.getValue()?.length || 0}</span>,
      }),
      columnHelper.accessor('creationDate', {
        header: 'Created',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateShort(info.getValue())}</span>,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const isActive = (info.row.original.status || 'active') === 'active';
          const isMutating = blockLender.isPending || unblockLender.isPending;
          return (
            <button
              onClick={(e) => handleToggleStatus(e, info.row.original)}
              disabled={isMutating}
              className={`text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ${isMutating ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isActive ? 'Block lender' : 'Unblock lender'}
            >
              {isActive ? (
                <ToggleRight className="h-5 w-5 text-green-500" />
              ) : (
                <ToggleLeft className="h-5 w-5 text-gray-400" />
              )}
            </button>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blockLender, unblockLender],
  );

  return (
    <div>
      <PageHeader
        title="Lenders"
        description="Manage lender organizations and their configurations"
        actions={
          <button onClick={() => navigate('/lenders/new')} className="btn-primary gap-1">
            <Plus className="h-4 w-4" />
            New Lender
          </button>
        }
      />
      <DataTable
        data={lenders || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(lender) => navigate(`/lenders/${encodeURIComponent(lender.urn)}`)}
      />
    </div>
  );
}
